import { useId } from 'react';
import { Monitor, Palette } from 'lucide-react';

type RailIconName = 'chat' | 'work' | 'agents' | 'data' | 'library' | 'tools' | 'settings';

// Rounded silhouettes share one optical size and stroke weight.
const shapes: Record<RailIconName, { outline: string; detail?: string }> = {
  chat: {
    outline: 'M4 10.2 10.2 5a2.8 2.8 0 0 1 3.6 0l6.2 5.2V18a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 18Z',
    detail: 'M9.5 20.5v-6a2.5 2.5 0 0 1 5 0v6',
  },
  work: {
    outline: 'M7 3.5h10A3.5 3.5 0 0 1 20.5 7v10a3.5 3.5 0 0 1-3.5 3.5H7A3.5 3.5 0 0 1 3.5 17V7A3.5 3.5 0 0 1 7 3.5Z',
    detail: 'M8 8.5h8M8 12h8M8 15.5h4.5',
  },
  agents: {
    outline: 'M10.65 4.4c.4-1.2 2.3-1.2 2.7 0l1.2 3.65a2.2 2.2 0 0 0 1.4 1.4l3.65 1.2c1.2.4 1.2 2.3 0 2.7l-3.65 1.2a2.2 2.2 0 0 0-1.4 1.4l-1.2 3.65c-.4 1.2-2.3 1.2-2.7 0l-1.2-3.65a2.2 2.2 0 0 0-1.4-1.4l-3.65-1.2c-1.2-.4-1.2-2.3 0-2.7l3.65-1.2a2.2 2.2 0 0 0 1.4-1.4Z',
  },
  data: {
    outline: 'M20.5 12a8.5 8.5 0 1 1-17 0 8.5 8.5 0 0 1 17 0Z',
    detail: 'M12 3.5V12h8.5M12 12l6 6',
  },
  library: {
    outline: 'M7 3.5h10A3.5 3.5 0 0 1 20.5 7v10a3.5 3.5 0 0 1-3.5 3.5H7A3.5 3.5 0 0 1 3.5 17V7A3.5 3.5 0 0 1 7 3.5Z',
    detail: 'M3.5 16.5 8 12a1.5 1.5 0 0 1 2.1 0l3.4 3.4 1.5-1.5a1.5 1.5 0 0 1 2.1 0l3.4 3.4M16.5 8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z',
  },
  tools: {
    outline: 'M8.5 4.5h1a2.5 2.5 0 1 1 5 0h2a3 3 0 0 1 3 3v2a2.5 2.5 0 1 1 0 5v2a3 3 0 0 1-3 3h-2a2.5 2.5 0 1 0-5 0h-2a3 3 0 0 1-3-3v-2a2.5 2.5 0 1 0 0-5v-2a3 3 0 0 1 3-3Z',
  },
  settings: {
    outline: 'M9 4.5c0-2 6-2 6 0a2 2 0 0 0 2.6 1.5c1.8-1 4.8 4.2 3 5.2a2 2 0 0 0 0 3c1.8 1-1.2 6.2-3 5.2A2 2 0 0 0 15 21c0 2-6 2-6 0a2 2 0 0 0-2.6-1.6c-1.8 1-4.8-4.2-3-5.2a2 2 0 0 0 0-3C1.6 10.2 4.6 5 6.4 6A2 2 0 0 0 9 4.5Z',
    detail: 'M15 12.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
  },
};

export function PrimaryRailIcon({ name, filled = false }: { name: RailIconName | 'site' | 'canvas'; filled?: boolean }) {
  const id = useId();
  if (name === 'site' || name === 'canvas') {
    const EditorIcon = name === 'site' ? Monitor : Palette;
    return <EditorIcon size={22} strokeWidth={filled ? 2 : 1.65} aria-hidden="true" />;
  }
  const { outline, detail } = shapes[name];
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {filled ? <>
        <defs><mask id={id}><path d={outline} fill="white" stroke="white" strokeWidth="1.5" />{detail ? <path d={detail} fill="none" stroke="black" strokeWidth="1.5" /> : null}</mask></defs>
        <rect width="24" height="24" fill="currentColor" mask={`url(#${id})`} />
      </> : <>
        <path d={outline} stroke="currentColor" strokeWidth="1.65" />
        {detail ? <path d={detail} stroke="currentColor" strokeWidth="1.65" /> : null}
      </>}
    </svg>
  );
}
